import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Accordion, Table } from '@mantine/core';
import NumberFormat from 'react-number-format';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import AppStack from '../../common/AppStack';
import { useRegCartController } from '../../../helpers/shop/useRegCartController';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppText from '../../common/AppText';
import { getCheckoutPaymentPlanInfo } from '../../../helpers/installmentsHelper';
import AppFlexbox from '../../common/AppFlexbox';
import { formatLocalDate, formatUtcDate } from '../../../helpers/format';

const CheckoutPaymentPlanSelectModal = ({
  isOpen,
  onClose,
  paymentPlans,
  onContinue
}) => {
  const { userCart, cartShippingController } = useRegCartController();
  const [formState, setFormState] = useState({
    paymentPlanId: paymentPlans[0]?.definition.id
  });

  useEffect(() => {
    if (isOpen) {
      setFormState({ paymentPlanId: paymentPlans[0]?.definition.id });
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      formSectionProps={{
        onSubmit: () => {
          onContinue(formState);
          onClose();
        },
        submitTitle: 'Continue to checkout',
        submitColor: 'blue'
      }}
      isNewModalStyle
      isOpen={isOpen}
      onClose={onClose}
      radius={0}
      size={500}
      title="Select Payment Plan"
    >
      <AppStack style={{ gap: 20 }}>
        <AppText
          style={{
            fontSize: 14,
            color: '#666',
            textAlign: 'center',
            fontStyle: 'italic'
          }}
        >
          Choose a payment plan that works best for you.
        </AppText>

        <AppStack>
          <Accordion
            defaultValue={paymentPlans[0]?.definition.id}
            onChange={(value) =>
              setFormState({ ...formState, paymentPlanId: value })
            }
            styles={{ content: { padding: 0 } }}
            value={formState.paymentPlanId}
            variant="contained"
          >
            {paymentPlans.map((plan) => {
              const selected = formState.paymentPlanId === plan.definition.id;
              const planInfo = getCheckoutPaymentPlanInfo(plan);

              return (
                <Accordion.Item
                  key={plan.definition.id}
                  style={selected ? { border: '2px solid dodgerblue' } : {}}
                  value={plan.definition.id}
                >
                  <Accordion.Control>
                    <AppFlexbox>
                      <AppStack>
                        <AppText
                          style={{
                            fontSize: 16,
                            fontWeight: 500
                          }}
                        >
                          {planInfo.paymentPlanLabel}
                        </AppText>

                        {planInfo.serviceFeeLabel && (
                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            This includes an additional{' '}
                            {planInfo.serviceFeeLabel} fee.
                          </AppText>
                        )}
                      </AppStack>
                    </AppFlexbox>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <AppStack style={{ gap: 0 }}>
                      <AppStack style={{ padding: 8 }}>
                        <Table withColumnBorders withRowBorders withTableBorder>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: 'rgba(56, 56, 56, 0.1)'
                                }}
                              >
                                <AppFlexbox
                                  style={{ gap: 16, alignItems: 'center' }}
                                >
                                  <AppText
                                    style={{ fontSize: 14, fontWeight: 500 }}
                                  >
                                    Date
                                  </AppText>
                                </AppFlexbox>
                              </Table.Th>

                              <Table.Th
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: 'rgba(56, 56, 56, 0.1)'
                                }}
                              >
                                <AppFlexbox
                                  style={{ gap: 16, alignItems: 'center' }}
                                >
                                  <AppText
                                    style={{ fontSize: 14, fontWeight: 500 }}
                                  >
                                    Amount
                                  </AppText>
                                </AppFlexbox>
                              </Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {plan.installments.map((installment) => {
                              const isToday =
                                new Date() > new Date(installment.chargeDate);

                              return (
                                <Table.Tr key={installment.entityId}>
                                  <Table.Td style={{ padding: '8px 16px' }}>
                                    <AppText
                                      style={{
                                        fontSize: 14,
                                        fontWeight: isToday ? 500 : 'normal'
                                      }}
                                    >
                                      {isToday
                                        ? 'Today'
                                        : dayjs(
                                            formatLocalDate(
                                              installment.chargeDate
                                            )
                                          ).format('MMM D, YYYY')}
                                    </AppText>
                                  </Table.Td>
                                  <Table.Td style={{ padding: '8px 16px' }}>
                                    <AppText
                                      style={{
                                        fontSize: 14,
                                        fontWeight: isToday ? 500 : 'normal'
                                      }}
                                    >
                                      <NumberFormat
                                        decimalScale={2}
                                        displayType="text"
                                        fixedDecimalScale
                                        prefix="$"
                                        thousandSeparator
                                        value={installment.totalInCents / 100}
                                      />
                                    </AppText>
                                  </Table.Td>
                                </Table.Tr>
                              );
                            })}
                          </Table.Tbody>
                        </Table>
                      </AppStack>
                    </AppStack>
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </AppStack>
      </AppStack>
    </ResponsiveModal>
  );
};

CheckoutPaymentPlanSelectModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onContinue: PropTypes.func,
  paymentPlans: PropTypes.array
};

export default CheckoutPaymentPlanSelectModal;
